import React, { useState } from 'react';
import { CheckCircle, XCircle, AlertCircle, Sparkles } from 'lucide-react';

const DiscrepancyResolutionQueue = () => {
  const [discrepancies, setDiscrepancies] = useState([
    {
      id: 1,
      type: 'Amount Mismatch',
      severity: 'high',
      expected: 1250.00,
      actual: 1234.50,
      difference: -15.50,
      suggestion: 'Auto-match with pending transaction #TXN-8821',
      confidence: 95,
      merchantId: 'M-1001',
      merchantName: 'TechStore Inc'
    },
    {
      id: 2,
      type: 'Missing Settlement',
      severity: 'medium',
      expected: 890.00,
      actual: 0,
      difference: -890.00,
      suggestion: 'Match with delayed batch settlement',
      confidence: 88,
      merchantId: 'M-1002',
      merchantName: 'Fashion Hub'
    },
    {
      id: 3,
      type: 'Duplicate Entry',
      severity: 'low',
      expected: 450.00,
      actual: 900.00,
      difference: 450.00,
      suggestion: 'Remove duplicate transaction #TXN-8819',
      confidence: 92,
      merchantId: 'M-1003',
      merchantName: 'Book World'
    }
  ]);

  const handleOneClickResolve = (discrepancyId) => {
    setDiscrepancies(prev =>
      prev?.filter(d => d?.id !== discrepancyId)
    );
  };

  const handleReject = (discrepancyId) => {
    setDiscrepancies(prev =>
      prev?.map(d =>
        d?.id === discrepancyId
          ? { ...d, status: 'rejected' }
          : d
      )
    );
  };

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'high':
        return 'bg-red-100 text-red-700 border-red-200';
      case 'medium':
        return 'bg-orange-100 text-orange-700 border-orange-200';
      case 'low':
        return 'bg-yellow-100 text-yellow-700 border-yellow-200';
      default:
        return 'bg-gray-100 text-gray-700 border-gray-200';
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
          <AlertCircle className="w-5 h-5 text-orange-600" />
          Discrepancy Resolution Queue
        </h3>
        <span className="px-2 py-1 bg-orange-100 text-orange-700 rounded-full text-xs font-medium">
          {discrepancies?.length} Pending
        </span>
      </div>
      <div className="space-y-3">
        {discrepancies?.map((discrepancy) => (
          <div
            key={discrepancy?.id}
            className="bg-gray-50 rounded-lg border border-gray-200 p-4"
          >
            {/* Header */}
            <div className="flex items-start justify-between mb-3">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <span className={`px-2 py-1 rounded text-xs font-medium border ${getSeverityColor(discrepancy?.severity)}`}>
                    {discrepancy?.type}
                  </span>
                </div>
                <div className="text-sm text-gray-600">
                  {discrepancy?.merchantName} ({discrepancy?.merchantId})
                </div>
              </div>
            </div>

            {/* Amount Comparison */}
            <div className="grid grid-cols-3 gap-2 mb-3 text-sm">
              <div className="bg-white rounded p-2 border border-gray-200">
                <div className="text-xs text-gray-500 mb-1">Expected</div>
                <div className="font-semibold text-gray-900">
                  ${discrepancy?.expected?.toFixed(2)}
                </div>
              </div>
              <div className="bg-white rounded p-2 border border-gray-200">
                <div className="text-xs text-gray-500 mb-1">Actual</div>
                <div className="font-semibold text-gray-900">
                  ${discrepancy?.actual?.toFixed(2)}
                </div>
              </div>
              <div className={`rounded p-2 border ${
                discrepancy?.difference < 0
                  ? 'bg-red-50 border-red-200' :'bg-green-50 border-green-200'
              }`}>
                <div className="text-xs text-gray-500 mb-1">Difference</div>
                <div className={`font-semibold ${
                  discrepancy?.difference < 0 ? 'text-red-700' : 'text-green-700'
                }`}>
                  ${Math.abs(discrepancy?.difference)?.toFixed(2)}
                </div>
              </div>
            </div>

            {/* AI Suggestion */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-3">
              <div className="flex items-start gap-2">
                <Sparkles className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
                <div className="flex-1">
                  <div className="text-xs font-medium text-blue-900 mb-1">
                    AI Suggestion (Confidence: {discrepancy?.confidence}%)
                  </div>
                  <div className="text-sm text-blue-800">
                    {discrepancy?.suggestion}
                  </div>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-2">
              <button
                onClick={() => handleOneClickResolve(discrepancy?.id)}
                className="flex-1 px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center justify-center gap-2 text-sm font-medium"
              >
                <CheckCircle className="w-4 h-4" />
                One-Click Resolve
              </button>
              <button
                onClick={() => handleReject(discrepancy?.id)}
                className="px-3 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 flex items-center gap-2 text-sm font-medium"
              >
                <XCircle className="w-4 h-4" />
                Reject
              </button>
            </div>
          </div>
        ))}
      </div>
      {discrepancies?.length === 0 && (
        <div className="text-center py-8 text-gray-500">
          <CheckCircle className="w-12 h-12 mx-auto mb-3 opacity-50" />
          <p>No discrepancies in queue</p>
          <p className="text-sm">All settlements reconciled successfully</p>
        </div>
      )}
    </div>
  );
};

export default DiscrepancyResolutionQueue;